import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface MuiElegantErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'error' | 'warning' | 'info';
}

export const MuiElegantErrorModal: React.FC<MuiElegantErrorModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'error'
}) => {
  const getIcon = () => {
    switch (type) {
      case 'error': return <ErrorIcon sx={{ fontSize: 48, color: '#d32f2f' }} />;
      case 'warning': return <WarningIcon sx={{ fontSize: 48, color: '#ed6c02' }} />;
      case 'info': return <InfoIcon sx={{ fontSize: 48, color: '#0288d1' }} />;
      default: return <ErrorIcon sx={{ fontSize: 48, color: '#d32f2f' }} />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'error': return '#ffebee';
      case 'warning': return '#fff8e1';
      case 'info': return '#e3f2fd';
      default: return '#ffebee';
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'error': return '#ffcdd2';
      case 'warning': return '#ffecb3';
      case 'info': return '#bbdefb';
      default: return '#ffcdd2';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'error': return '#d32f2f';
      case 'warning': return '#ed6c02';
      case 'info': return '#0288d1';
      default: return '#d32f2f';
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 3 }}>
        <Box
          sx={{
            backgroundColor: getBackgroundColor(),
            border: `2px solid ${getBorderColor()}`,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            {getIcon()}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: '#374151',
                lineHeight: 1.6,
                whiteSpace: 'pre-line',
                fontSize: '0.95rem',
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: getButtonColor(),
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '&:hover': {
              backgroundColor: getButtonColor(),
              filter: 'brightness(0.9)',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Understood
        </Button>
      </DialogActions>
    </Dialog>
  );
};